import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  ArrowLeft, 
  Mail, 
  Phone,
  User,
  Sparkles,
  Heart,
  Crown
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const POSITION_INFO = {
  shepherd: { 
    title: "Shepherd", 
    description: "Spiritual leadership and pastoral care",
    gradient: "from-purple-500 to-indigo-600",
    lightBg: "bg-purple-50",
    icon: "🙏"
  },
  welfare_head: { 
    title: "Usher and Sidemen Department", 
    description: "Overseeing church welfare and usher ministry",
    gradient: "from-amber-500 to-orange-500",
    lightBg: "bg-amber-50",
    icon: "🤝"
  },
  choir_master: { 
    title: "Choir Department", 
    description: "Leading worship through music and song",
    gradient: "from-pink-500 to-rose-500",
    lightBg: "bg-pink-50",
    icon: "🎵"
  },
  sunday_school_teacher: { 
    title: "Sunday School", 
    description: "Nurturing the next generation in faith",
    gradient: "from-green-500 to-emerald-500",
    lightBg: "bg-green-50",
    icon: "📚"
  }
};

const MeetOurTeamPage = () => {
  const [teamData, setTeamData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const response = await axios.get(`${API}/team/public`);
      setTeamData(response.data);
    } catch (error) {
      console.error('Error loading team:', error);
    }
    setLoading(false);
  };

  const hasAnyMembers = Object.values(teamData).some(group => group?.members?.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-white/90 border-b border-slate-200/50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/"
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Home</span>
            </Link>
            
            <div className="flex items-center gap-3">
              <motion.img 
                src="/images/church-logo.png?v=2" 
                alt="CCC VOB PARISH Logo" 
                className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-lg"
                whileHover={{ scale: 1.1 }}
              />
              <span className="hidden sm:inline font-semibold text-slate-700">CCC VOB Parish</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=1920')] bg-cover bg-center opacity-20" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm mb-6">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span className="text-sm font-medium">Our Leadership Team</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Meet Our Team
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
              Dedicated servants of God leading our parish with faith, wisdom, and love.
            </p>
          </motion.div>
        </div>
        
        {/* Wave decoration */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 100V50C240 0 480 100 720 50C960 0 1200 100 1440 50V100H0Z" fill="rgb(248 250 252)" />
          </svg>
        </div>
      </section>

      {/* Team Sections */}
      <section className="py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          ) : !hasAnyMembers ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <Users className="w-20 h-20 mx-auto text-slate-300 mb-6" />
              <h3 className="text-2xl font-bold text-slate-700 mb-3">Team Coming Soon</h3>
              <p className="text-slate-500 max-w-md mx-auto">
                Our leadership team information is being updated. Please check back soon!
              </p>
            </motion.div>
          ) : (
            <div className="space-y-16">
              {Object.entries(teamData).map(([positionId, group], groupIndex) => {
                if (!group?.members?.length) return null;
                
                const posInfo = POSITION_INFO[positionId] || {
                  title: group.title,
                  description: "",
                  gradient: "from-slate-500 to-slate-600",
                  lightBg: "bg-slate-50",
                  icon: "👤"
                };
                
                return (
                  <motion.div
                    key={positionId}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: groupIndex * 0.1 }}
                  >
                    {/* Section Header */}
                    <div className="text-center mb-10">
                      <div className={`inline-flex items-center gap-3 px-5 py-2.5 rounded-full ${posInfo.lightBg} mb-4`}>
                        <span className="text-2xl">{posInfo.icon}</span>
                        <h2 className="text-xl font-bold text-slate-800">{posInfo.title}</h2>
                      </div>
                      {posInfo.description && (
                        <p className="text-slate-500 max-w-lg mx-auto">{posInfo.description}</p>
                      )}
                    </div>
                    
                    {/* Members Grid */}
                    <div className={`grid gap-6 ${
                      group.members.length === 1 
                        ? 'grid-cols-1 max-w-md mx-auto' 
                        : group.members.length === 2
                          ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto'
                          : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                    }`}>
                      {group.members.map((member, memberIndex) => (
                        <TeamMemberCard 
                          key={member.id} 
                          member={member} 
                          posInfo={posInfo}
                          delay={memberIndex * 0.1}
                        />
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <motion.div 
            className="flex items-center justify-center gap-2 text-slate-400 mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <span>Built with</span>
            <Heart className="w-4 h-4 text-red-500" />
            <span>for the Kingdom</span>
            <Crown className="w-4 h-4 text-yellow-500" />
          </motion.div>
          <p className="text-sm text-slate-500">
            Celestial Church of Christ — Valley of Berachah Parish
          </p>
        </div>
      </footer>
    </div>
  );
};

// Team Member Card Component
const TeamMemberCard = ({ member, posInfo, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      whileHover={{ y: -5 }}
      className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100 hover:shadow-xl transition-all duration-300"
    >
      {/* Photo */}
      <div className={`relative h-56 bg-gradient-to-br ${posInfo.gradient}`}>
        {member.photo_url ? (
          <img
            src={member.photo_url.startsWith('/') 
              ? `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}${member.photo_url}`
              : member.photo_url}
            alt={member.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-24 h-24 text-white/30" />
          </div>
        )}
        
        {/* Gradient overlay at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />
      </div>
      
      {/* Info */}
      <div className="p-5 -mt-8 relative">
        <div className="bg-white rounded-xl shadow-md p-4 text-center">
          <h3 className="font-bold text-lg text-slate-800 mb-1">{member.name}</h3>
          <p className={`text-sm font-medium bg-gradient-to-r ${posInfo.gradient} bg-clip-text text-transparent`}>
            {member.title}
          </p>
          
          {member.bio && (
            <p className="text-slate-500 text-sm mt-3 line-clamp-2">{member.bio}</p>
          )}
          
          {/* Contact info */}
          <div className="mt-4 pt-4 border-t border-slate-100 flex justify-center gap-4">
            {member.email && (
              <a 
                href={`mailto:${member.email}`}
                className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                title={member.email}
              >
                <Mail className="w-4 h-4" />
              </a>
            )}
            {member.phone && (
              <a 
                href={`tel:${member.phone}`}
                className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                title={member.phone}
              >
                <Phone className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MeetOurTeamPage;
